import { api } from "./apiSlice";
import { showMessage } from "../../helpers";

export const approvalRequestsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getApprovalRequests: builder.query({
            query: (params = {}) => {
                const {
                    page,
                    page_size,
                    limit,
                    offset,
                    ordering,
                    search,
                    partner = [],
                    requestType = [],
                    license = [],
                    status = [],
                    ...otherParams
                } = params;

                let url = "/partner/approval-requests/";
                const queryParams = new URLSearchParams();

                // Pagination - prefer limit/offset
                if (limit !== undefined) {
                    queryParams.append('limit', limit);
                } else if (page_size !== undefined) {
                    queryParams.append('limit', page_size);
                }

                if (offset !== undefined) {
                    queryParams.append('offset', offset);
                } else if (page !== undefined && page_size !== undefined) {
                    const calculatedOffset = (page - 1) * page_size;
                    queryParams.append('offset', calculatedOffset);
                }

                // Search
                if (search) {
                    queryParams.append('search', search);
                }

                // Sorting
                if (ordering) {
                    queryParams.append('ordering', ordering);
                }

                // Array filters - using correct parameter names and sending multiple values as comma-separated
                if (partner.length > 0) {
                    queryParams.append('partner', partner.join(','));
                }
                if (requestType.length > 0) {
                    queryParams.append('approval_request_type', requestType.join(','));
                }
                if (license.length > 0) {
                    queryParams.append('license', license.join(','));
                }
                if (status.length > 0) {
                    queryParams.append('status', status.join(','));
                }

                // Remaining params
                Object.entries(otherParams).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        // If array, append as comma-separated
                        if (Array.isArray(value)) {
                            if (value.length > 0) {
                                queryParams.append(key, value.join(','));
                            }
                        } else {
                            queryParams.append(key, value);
                        }
                    }
                });

                // Build final URL
                const queryString = queryParams.toString();
                if (queryString) {
                    url += `?${queryString}`;
                }

                return url;
            },
            providesTags: ["ApprovalRequests"],
        }),

        getApprovalRequestLicenses: builder.query({
            query: (params = {}) => {
                const {
                    page,
                    page_size,
                    limit,
                    offset,
                    ordering,
                    search,
                    ...otherParams
                } = params;

                let url = "/partner/approval-request-licenses/";
                const queryParams = new URLSearchParams();

                // Pagination - prefer limit/offset
                if (limit !== undefined) {
                    queryParams.append('limit', limit);
                } else if (page_size !== undefined) {
                    queryParams.append('limit', page_size);
                }

                if (offset !== undefined) {
                    queryParams.append('offset', offset);
                } else if (page !== undefined && page_size !== undefined) {
                    const calculatedOffset = (page - 1) * page_size;
                    queryParams.append('offset', calculatedOffset);
                }

                // Search
                if (search) {
                    queryParams.append('search', search);
                }

                // Sorting
                if (ordering) {
                    queryParams.append('ordering', ordering);
                }

                // Remaining params
                Object.entries(otherParams).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        queryParams.append(key, value);
                    }
                });

                // Build final URL
                const queryString = queryParams.toString();
                if (queryString) {
                    url += `?${queryString}`;
                }

                return url;
            },
            providesTags: ["ApprovalRequests"],
        }),

        getApprovalRequestsFilters: builder.query({
            query: () => "/partner/approval-requests/filters/",
            providesTags: ["ApprovalRequests"],
        }),

        getApprovalRequestsOptions: builder.query({
            query: () => "/partner/approval-requests/options/",
            providesTags: ["ApprovalRequests"],
        }),

        // GET single approval request by ID
        getApprovalRequestById: builder.query({
            query: (approval_request_id) => `/partner/approval-requests/${approval_request_id}/`,
            providesTags: (result, error, id) => [{ type: "ApprovalRequests", id }],
        }),

        // POST new approval request with form-data
        createApprovalRequest: builder.mutation({
            query: (formData) => ({
                url: "/partner/approval-requests/",
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["ApprovalRequests"],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                    showMessage( "success", "Approval request created successfully!");
                } catch (error) {
                    showMessage("error", "Failed to create approval request");
                }
            },
        }),

        // PUT update approval request by ID with FormData to handle file operations
        updateApprovalRequest: builder.mutation({
            query: ({ id, data }) => ({
                url: `/partner/approval-requests/${id}/`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                "ApprovalRequests",
                { type: "ApprovalRequests", id }
            ],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                    showMessage( "success", "Approval request updated successfully!");
                } catch (error) {
                    showMessage("error", "Failed to update approval request");
                }
            },
        }),

        // POST approve approval request by ID with FormData
        approveApprovalRequest: builder.mutation({
            query: ({ id, data }) => ({
                url: `/partner/approval-requests/${id}/approve/`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                "ApprovalRequests",
                { type: "ApprovalRequests", id }
            ],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                    showMessage("success","Approval request approved successfully!");
                } catch (error) {
                    showMessage("error", "Failed to approve approval request");
                }
            },
        }),

        // POST reject approval request by ID with FormData
        rejectApprovalRequest: builder.mutation({
            query: ({ id, data }) => ({
                url: `/partner/approval-requests/${id}/reject/`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                "ApprovalRequests",
                { type: "ApprovalRequests", id }
            ],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                    showMessage("success", "Approval request rejected successfully!");
                } catch (error) {
                    showMessage("error", "Failed to reject approval request");
                }
            },
        }),

        // POST needs-revision for approval request by ID with FormData
        needsRevisionApprovalRequest: builder.mutation({
            query: ({ id, data }) => ({
                url: `/partner/approval-requests/${id}/needs-revision/`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                "ApprovalRequests",
                { type: "ApprovalRequests", id }
            ],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                    showMessage("success", "Approval request marked as needs revision!");
                } catch (error) {
                    showMessage("error", "Failed to mark approval request as needs revision");
                }
            },
        }),

        // POST submit approval request by ID with FormData
        submitApprovalRequest: builder.mutation({
            query: ({ id, data }) => ({
                url: `/partner/approval-requests/${id}/submit/`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                "ApprovalRequests",
                { type: "ApprovalRequests", id }
            ],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                    showMessage("success", "Approval request submitted successfully!");
                } catch (error) {
                    showMessage("error", "Failed to submit approval request");
                }
            },
        }),

        // POST send reminder for approval request by ID (empty body)
        sendReminderApprovalRequest: builder.mutation({
            query: (id) => ({
                url: `/partner/approval-requests/${id}/send-reminder/`,
                method: "POST",
                body: {}, // Empty body as requested
            }),
            invalidatesTags: (result, error, id) => [
                "ApprovalRequests",
                { type: "ApprovalRequests", id }
            ],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                    showMessage("success", "Reminder sent successfully!");
                } catch (error) {
                    showMessage("error", "Failed to send reminder");
                }
            },
        }),

        // LAZY QUERY for downloading approval requests report
        downloadApprovalRequestsReport: builder.query({
            query: () => ({
                url: "/partner/approval-requests/download/",
                method: "POST",
                body: {}, // Empty body as requested
                responseHandler: async (response) => {
                    return await response.blob();
                },
            }),
        }),
    }),
});

export const {
    useGetApprovalRequestsQuery,
    useGetApprovalRequestLicensesQuery,
    useGetApprovalRequestsFiltersQuery,
    useGetApprovalRequestsOptionsQuery,
    useGetApprovalRequestByIdQuery,
    useCreateApprovalRequestMutation,
    useUpdateApprovalRequestMutation,
    useApproveApprovalRequestMutation,
    useRejectApprovalRequestMutation,
    useNeedsRevisionApprovalRequestMutation,
    useSubmitApprovalRequestMutation,
    useSendReminderApprovalRequestMutation,
    useLazyDownloadApprovalRequestsReportQuery
} = approvalRequestsApi;